import { recordingNotices, regionSchema, type Me, type RecordingNotice, type RegionSummary } from '@smarton-pbx/shared';
import { MapPin, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { api } from '../api';
import { Dialog, EmptyState, Field, Loading, Menu, Notice, opensOnClick, PageHeader, Pill } from '../components/ui';
import { CountryListInput, CountrySelect, flag, LanguageListInput, TimeZoneSelect } from '../components/pickers';
import { type Column, Cells, mayDownload, TableHead, useTable } from '../components/table';
import { t, tc } from '../i18n';
import { useLoad, useSubmit } from '../lib/hooks';

const noticeNames: Record<RecordingNotice, string> = {
  NONE: t('No notice'),
  ANNOUNCE: t('Announced to the other party'),
  REMIND: t('Reminder to staff'),
};

function regionColumns(): Column<RegionSummary>[] {
  return [
    {
      key: 'region',
      label: t('Region'),
      fixed: true,
      cell: (region) => (
        <div className="person">
          <span className="avatar square" style={{ fontSize: '1.15rem', background: 'var(--surface-2)' }} aria-hidden>
            {flag(region.countryCode) || region.countryCode}
          </span>
          <div>
            <div className="name">{region.name}</div>
            <div className="detail">{region.countryCode}</div>
          </div>
        </div>
      ),
      excel: [
        { label: t('Region'), value: (region) => region.name },
        { label: t('Country'), value: (region) => region.countryCode },
      ],
    },
    { key: 'timeZone', label: t('Time zone'), className: 'hide-mobile muted', cell: (region) => region.timeZone, value: (region) => region.timeZone },
    {
      key: 'languages',
      label: t('Languages'),
      cell: (region) => (
        <div className="row" style={{ gap: '0.3rem' }}>
          {region.languages.map((language, i) => (
            <Pill key={language} {...(i === 0 && { kind: 'brand' as const })}>
              {language.toUpperCase()}
            </Pill>
          ))}
        </div>
      ),
      value: (region) => region.languages.map((l) => l.toUpperCase()).join(', '),
    },
    {
      key: 'recordingNotice',
      label: t('Recording notice'),
      className: 'hide-mobile muted',
      cell: (region) => noticeNames[region.recordingNotice],
      value: (region) => noticeNames[region.recordingNotice],
    },
    { key: 'people', label: t('People'), className: 'tabular', cell: (region) => region.userCount, value: (region) => region.userCount },
    {
      key: 'retention',
      label: t('Keep recordings for (days)'),
      hidden: true,
      className: 'hide-mobile tabular',
      cell: (region) => region.retentionDays,
      value: (region) => region.retentionDays,
    },
    {
      key: 'countries',
      label: t('Other countries staff may call'),
      hidden: true,
      className: 'hide-mobile muted',
      cell: (region) => region.allowedDestinationCountries.join(', ') || '—',
      value: (region) => region.allowedDestinationCountries.join(', '),
    },
  ];
}

export function Regions({ me }: { me: Me }) {
  const regions = useLoad(api.listRegions);
  const [editing, setEditing] = useState<RegionSummary | 'new' | null>(null);
  const [error, setError] = useState<string>();
  const isAdmin = me.role === 'TENANT_ADMIN';
  const table = useTable('regions', regionColumns(), mayDownload(me.role) ? { name: t('Regions'), rows: () => regions.data ?? [] } : null);

  async function remove(region: RegionSummary) {
    if (!confirm(t('Delete the region {name}?', { name: region.name }))) return;
    try {
      await api.deleteRegion(region.id);
      regions.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const addButton = isAdmin && (
    <button type="button" onClick={() => setEditing('new')} data-testid="add-region">
      <Plus size={16} aria-hidden /> {t('Add region')}
    </button>
  );

  return (
    <div className="stack">
      <PageHeader title={t('Regions')} description={t('Countries or offices. Each has its own languages, recording notice and allowed destinations.')}>
        {addButton}
      </PageHeader>
      {(error ?? regions.error) && <Notice kind="error">{error ?? regions.error}</Notice>}
      {!regions.data ? (
        <Loading />
      ) : regions.data.length === 0 ? (
        <div className="card">
          <EmptyState icon={MapPin} title={t('No regions yet')}>
            <p>{t('Add one for each country or office, then add people to it.')}</p>
          </EmptyState>
        </div>
      ) : (
        <div className="card table-wrap">
          <table>
            <TableHead table={table} />
            <tbody>
              {regions.data.map((region) => (
                <tr key={region.id} data-testid="region-row" {...(isAdmin && opensOnClick(() => setEditing(region)))}>
                  <Cells table={table} row={region} />
                  <td className="actions">
                    {isAdmin && (
                      <Menu
                        label={t('Actions for {name}', { name: region.name })}
                        trigger={(props) => (
                          <button type="button" className="ghost icon" aria-label={t('Actions for {name}', { name: region.name })} {...props}>
                            <MoreHorizontal size={18} aria-hidden />
                          </button>
                        )}
                      >
                        {(close) => (
                          <>
                            <button type="button" role="menuitem" onClick={() => (close(), setEditing(region))}>
                              <Pencil size={16} aria-hidden /> {t('Edit')}
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="danger-item"
                              disabled={region.userCount > 0}
                              title={region.userCount > 0 ? t('Move its people to another region first') : undefined}
                              onClick={() => (close(), void remove(region))}
                            >
                              <Trash2 size={16} aria-hidden /> {t('Delete')}
                            </button>
                          </>
                        )}
                      </Menu>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={editing !== null} onClose={() => setEditing(null)} title={editing === 'new' ? t('Add region') : editing ? t('Edit {name}', { name: editing.name }) : ''}>
        {editing && (
          <RegionForm
            region={editing === 'new' ? null : editing}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              regions.reload();
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

function RegionForm({ region, onCancel, onSaved }: { region: RegionSummary | null; onCancel: () => void; onSaved: () => void }) {
  const [name, setName] = useState(region?.name ?? '');
  const [countryCode, setCountryCode] = useState(region?.countryCode ?? '');
  const [timeZone, setTimeZone] = useState(region?.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [languages, setLanguages] = useState<string[]>(region?.languages ?? []);
  const [recordingNotice, setRecordingNotice] = useState<RecordingNotice>(region?.recordingNotice ?? 'NONE');
  const [retentionDays, setRetentionDays] = useState(String(region?.retentionDays ?? 365));
  const [allowed, setAllowed] = useState<string[]>(region?.allowedDestinationCountries ?? []);
  const form = useSubmit();

  async function submit(event: FormEvent) {
    event.preventDefault();
    const input = {
      name,
      countryCode,
      timeZone,
      languages,
      recordingNotice,
      retentionDays: Number(retentionDays),
      allowedDestinationCountries: allowed,
    };
    const saved = await form.submit(regionSchema, input, (value) => (region ? api.updateRegion(region.id, value) : api.createRegion(value)));
    if (saved) onSaved();
  }

  return (
    <form onSubmit={submit} noValidate>
      <div className="dialog-body">
        <div className="form-grid">
          <Field label={tc('thing', 'Name')} error={form.fields.name}>
            <input value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="region-name" />
          </Field>
          <Field label={t('Country')} hint={t('Numbers dialled without a country code are read as this country’s')} error={form.fields.countryCode}>
            <CountrySelect value={countryCode} onChange={setCountryCode} testId="region-country" />
          </Field>
          <Field label={t('Time zone')} hint={t('For reports and opening hours')} error={form.fields.timeZone}>
            <TimeZoneSelect value={timeZone} onChange={setTimeZone} />
          </Field>

          <Field label={t('Recording notice')} error={form.fields.recordingNotice}>
            <select value={recordingNotice} onChange={(e) => setRecordingNotice(e.target.value as RecordingNotice)}>
              {recordingNotices.map((notice) => (
                <option key={notice} value={notice}>
                  {noticeNames[notice]}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('Keep recordings for (days)')} hint={t('Then they’re deleted with their transcripts')} error={form.fields.retentionDays}>
            <input inputMode="numeric" value={retentionDays} onChange={(e) => setRetentionDays(e.target.value)} />
          </Field>
        </div>
        <Field group label={t('Call languages')} hint={t('The first is the main language; calls are transcribed in these.')} error={form.fields.languages}>
          <LanguageListInput value={languages} onChange={setLanguages} />
        </Field>
        <Field
          group
          label={t('Other countries staff may call')}
          hint={t('Calls within the region’s own country are always allowed. Calls to other countries not listed here are refused.')}
          error={form.fields.allowedDestinationCountries}
        >
          <CountryListInput value={allowed} onChange={setAllowed} placeholder={t('Add a country')} />
        </Field>
        {form.error && <Notice kind="error">{form.error}</Notice>}
      </div>
      <div className="dialog-footer">
        <button type="button" className="secondary" onClick={onCancel}>
          {t('Cancel')}
        </button>
        <button type="submit" disabled={form.busy} data-testid="save-region">
          {region ? t('Save changes') : t('Add region')}
        </button>
      </div>
    </form>
  );
}
